/**
 * BarList — the mockup's horizontal bar list: aging buckets (C3/C6), "קטגוריות מול בסיס"
 * (spec §4.5), income by service line and top customers.
 *
 * HTML, not SVG, so the bars are RTL-native and every row can be a real button. Rules:
 *  - one shared scale across the rows (the largest value or marker + 5%);
 *  - a baseline marker is a thin rule with a `title`, never a second colour;
 *  - a row that filters something is a `<button>`; a per-row extra control (the threshold
 *    pencil) is a SIBLING, never nested inside it (spec §8 "סמנטיקה").
 */

import type { ReactNode } from 'react';

export interface BarItem {
  key: string;
  label: ReactNode;
  /** agorot (or any positive unit — the scale is shared). */
  value: number;
  valueLabel: ReactNode;
  /** Ordinal ramp class (`o1`…`o4`) or a series class (`s2`, `s3`). */
  barClass?: string;
  /** Primary marker (the baseline / prorated baseline). */
  marker?: number;
  markerTitle?: string;
  /** Secondary, fainter marker (the full-month baseline in a running month). */
  marker2?: number;
  marker2Title?: string;
  /** Hover/focus title for the whole row. */
  title?: string;
  onClick?: () => void;
  pressed?: boolean;
  /** A sibling control rendered beside the row (never inside the button). */
  extra?: ReactNode;
}

export function BarList({ items, ariaLabel }: { items: BarItem[]; ariaLabel: string }) {
  const scale = Math.max(1, ...items.map((i) => Math.max(i.value, i.marker ?? 0, i.marker2 ?? 0))) * 1.05;
  const interactive = items.some((i) => i.onClick);
  const pct = (v: number) => `${((v / scale) * 100).toFixed(1)}%`;

  return (
    <div className="bars" role={interactive ? 'group' : 'list'} aria-label={ariaLabel}>
      {items.map((item) => {
        const inner = (
          <>
            <span className="bl">{item.label}</span>
            <span className="bt">
              <i className={item.barClass} style={{ width: pct(item.value) }} />
              {item.marker != null ? <span className="bm" style={{ insetInlineStart: pct(item.marker) }} title={item.markerTitle} /> : null}
              {item.marker2 != null ? <span className="bm faint" style={{ insetInlineStart: pct(item.marker2) }} title={item.marker2Title} /> : null}
            </span>
            <span className="bv">{item.valueLabel}</span>
          </>
        );
        const row = item.onClick ? (
          <button type="button" className="bar-row link" aria-pressed={item.pressed ?? false} title={item.title} onClick={item.onClick} key={item.key}>
            {inner}
          </button>
        ) : (
          <div className="bar-row" role={interactive ? undefined : 'listitem'} title={item.title} key={item.key}>
            {inner}
          </div>
        );
        return item.extra ? (
          <div className="bar-line" key={`${item.key}-line`}>
            {row}
            <span className="bar-x">{item.extra}</span>
          </div>
        ) : (
          row
        );
      })}
    </div>
  );
}
